// ── ConfirmSheet — the app's only way to ask "are you sure?" ──────────────────
// Before this, every confirmation was hand-rolled: six different shapes across the app,
// including one bare window.confirm that rendered as a browser alert in a fully styled
// product. The absence of a shared control WAS the bug — it's why removing a show is
// gated behind a sheet in MyList but bare in ShowDetail and bare again in
// QuickActionSheet. Same action, three answers. (Audit + Jason 2026-07-30.)
//
// Extracted from MyList's sheet, which was the best of the six: house-style bottom sheet,
// tap-outside to cancel, the SAFE choice on the left, the destructive one coloured.
//
// THE POLICY this exists to enforce:
//   * Irreversible + bulk (Clear all, delete a list, delete written content) → ConfirmSheet.
//   * Irreversible + single item → ConfirmSheet, unless an Undo is offered instead.
//   * Reversible toggles (follow, plan tonight, un-plan) → NEITHER. A confirm on a toggle
//     is nagging; a mis-tap costs one tap to undo.
//   * Undo BEATS a confirm wherever it's practical: it doesn't interrupt the common case,
//     which is the whole point of the common case. See UndoToast/toastRail.
//
// Usage:
//   const [confirm, setConfirm] = useState(false)
//   <ConfirmSheet open={confirm} emoji="🗑️" title={`Remove ${name}?`}
//     body="Your rating and progress will be kept."
//     confirmLabel="Remove" cancelLabel="Keep it"
//     onConfirm={() => { doIt(); setConfirm(false) }} onCancel={() => setConfirm(false)} />
import { Sheet } from './UI'

export function ConfirmSheet({
  open, emoji = '⚠️', title, body,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  tone = 'danger',              // 'danger' (destructive) | 'neutral'
  onConfirm, onCancel, z,
}) {
  // Was its own hand-rolled position:fixed overlay (no portal) — nested inside a
  // scrollable ancestor (e.g. NotificationBell's overflow-y panel), iOS Safari drags a
  // non-portaled "fixed" element along with that ancestor's scroll instead of pinning it
  // to the viewport, so the dialog rendered trapped mid-list instead of centered over the
  // screen (caught by Jason, screenshot, 2026-08-06). <Sheet> portals to document.body and
  // brings scroll-lock/focus-trap/Escape for free — exactly what this file used to
  // reimplement by hand and got wrong. See [[sheet-primitive-and-scroll-lock]].
  if (!open) return null

  const danger = tone === 'danger'
  return (
    <Sheet open={open} onClose={onCancel} label={title} from="bottom" {...(z ? { z } : {})}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        {emoji && <div style={{ fontSize: 34, marginBottom: 8 }}>{emoji}</div>}
        <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 16 }}>{title}</div>
        {body && <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{body}</div>}
      </div>
      {/* Safe choice FIRST and visually quieter. The destructive button is never the
          one your thumb lands on by default. */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => onCancel?.()}
          style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', color: 'var(--text)', fontWeight: 600,
            fontSize: 15, padding: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}>
          {cancelLabel}
        </button>
        <button onClick={() => onConfirm?.()}
          style={{ flex: 1,
            background: danger ? 'color-mix(in srgb, var(--state-bad) 15%, transparent)' : 'var(--amber)',
            border: danger ? '1px solid color-mix(in srgb, var(--state-bad) 35%, transparent)' : 'none',
            borderRadius: 'var(--radius-md)',
            color: danger ? 'var(--state-bad)' : 'var(--amber-ink)',
            fontWeight: 700, fontSize: 15, padding: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}>
          {confirmLabel}
        </button>
      </div>
    </Sheet>
  )
}
